import {createJSONStorage} from 'jotai/utils'

/**
 * Creates a storage adapter that migrates from recoil-persist to Jotai per-atom storage.
 *
 * On first access, checks for data in the old `localStorage['recoil-persist']` structure
 * and migrates it to the new per-atom key format that Jotai expects.
 *
 * @param {string} atomKey - The key used for both the new Jotai storage and looking up
 *                          the value in the old recoil-persist object
 * @returns {Storage} A Jotai-compatible storage object with migration fallback
 */
export function createMigrationStorage(atomKey) {
  // Create base SSR-safe storage
  const baseStorage = createJSONStorage(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      return localStorage
    }
    return {
      getItem: () => null,
      setItem: () => {},
      removeItem: () => {}
    }
  })

  // Override getItem to add migration fallback
  return {
    ...baseStorage,
    getItem: (key, initialValue) => {
      // First check the new per-atom Jotai key
      const newValue = baseStorage.getItem(key, initialValue)

      // If we got a value (not the default), migration already happened or value was set by Jotai
      if (newValue !== initialValue) {
        return newValue
      }

      // Try to migrate from old recoil-persist structure
      if (typeof window !== 'undefined' && window.localStorage) {
        try {
          const oldData = localStorage.getItem('recoil-persist')
          if (oldData) {
            const parsed = JSON.parse(oldData)
            if (Object.prototype.hasOwnProperty.call(parsed, atomKey)) {
              const migratedValue = parsed[atomKey]
              // Write to new storage location
              baseStorage.setItem(key, migratedValue)
              console.log(`[jotai-migration] Migrated ${atomKey} from recoil-persist to per-atom storage`)
              return migratedValue
            }
          }
        } catch (error) {
          // Silently handle malformed JSON or other errors
          console.debug(`[jotai-migration] Could not migrate ${atomKey}:`, error.message)
        }
      }

      return initialValue
    }
  }
}
